import { IonIcon } from '@ionic/react';
import { notifications, notificationsOffOutline } from 'ionicons/icons';
import React from 'react';
import { useCreateFollow } from '../../api/mutations/createFollow';
import { useDeleteFollow } from '../../api/mutations/deleteFollow';
import { useUser } from '../../contexts/UserContext';
import { ToastMsg } from '../../layouts/ToastMsg';
import { AirQualityMarkerDto, DatasourceId, FollowDto, NetworkDto } from '../../types';
import { LoginModal } from '../LoginModal';
import { Spinner } from '../Spinner';
import { ToolbarButton } from '../ToolbarButton';

interface FollowDatasourceButtonProps {
  marker: AirQualityMarkerDto;
  network: NetworkDto;
}

export const FollowDatasourceButton: React.FC<FollowDatasourceButtonProps> = ({ marker, network }) => {
  const { user, idToken, follows, isFollowDataLoading } = useUser();
  const createFollowMutation = useCreateFollow();
  const deleteFollowMutation = useDeleteFollow();
  const [isLoginModalOpen, setIsLoginModalOpen] = React.useState(false);

  const followsByDatasourceId = React.useMemo(() => {
    const map: Record<DatasourceId, FollowDto> = {};
    for (const follow of follows ?? []) {
      map[follow.datasourceId] = follow;
    }
    return map;
  }, [follows]);

  const isFollowing = React.useMemo(() => {
    return followsByDatasourceId[marker.datasourceId] !== undefined;
  }, [followsByDatasourceId, marker]);

  const changeFollowState = React.useCallback(
    (shouldFollow: boolean) => {
      if (!idToken) {
        return null;
      }

      if (!shouldFollow) {
        const follow = followsByDatasourceId[marker.datasourceId];
        deleteFollowMutation.mutate({ followId: follow.id, firebaseToken: idToken });
      } else {
        createFollowMutation.mutate({
          payload: {
            datasourceId: marker.datasourceId,
            networkId: network.networkId,
          },
          firebaseToken: idToken,
        });
      }
    },
    [marker, network, idToken, createFollowMutation, followsByDatasourceId]
  );

  const onClickBellButton = React.useCallback(() => {
    if (!user) {
      window.localStorage.setItem('wlom:datasourceToFollow', marker.datasourceId);
      setIsLoginModalOpen(true);
      return;
    }
    changeFollowState(!followsByDatasourceId[marker.datasourceId]);
  }, [user, followsByDatasourceId, marker, changeFollowState]);

  // Close the login modal if the user logs in
  React.useEffect(() => {
    if (isLoginModalOpen && user) {
      setIsLoginModalOpen(false);
    }
  }, [isLoginModalOpen, user]);

  // Check if a datasource should be followed after login
  React.useEffect(() => {
    if (!user) {
      return;
    }

    const dsToFollow = window.localStorage.getItem('wlom:datasourceToFollow');
    if (dsToFollow !== marker.datasourceId) {
      return;
    }
    window.localStorage.removeItem('wlom:datasourceToFollow');

    changeFollowState(true);
  }, [user, changeFollowState]);

  return (
    <>
      {isFollowDataLoading || createFollowMutation.isPending || deleteFollowMutation.isPending ? (
        <Spinner type="crescent" />
      ) : (
        <ToolbarButton id="follow-ds-details" onClick={onClickBellButton}>
          <IonIcon icon={isFollowing ? notifications : notificationsOffOutline} slot="icon-only" />
        </ToolbarButton>
      )}
      <LoginModal
        isOpen={isLoginModalOpen}
        setIsOpen={setIsLoginModalOpen}
        title={`Follow ${marker.datasourceName}`}
        promptText={`Log in or sign up to get notified when the air quality in ${marker.datasourceName} is unhealthy.`}
      />
      <ToastMsg
        isOpen={createFollowMutation.isSuccess}
        message={`You are now following ${marker.datasourceName}. You will receive an email if air quality becomes unhealthy.`}
        type="success"
        icon={notifications}
      />
    </>
  );
};
